/**
 * Auth Service
 * 
 * Centralized API calls for authentication operations.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Login with email/password
 * @param {string} email 
 * @param {string} password 
 * @returns {Promise<object>}
 */
export const login = async (email, password) => {
  // Use XMLHttpRequest to bypass any fetch interceptors (analytics, service workers,
  // devtools wrappers) that may consume the response body before we can read it.
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BACKEND_URL}/api/auth/login`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    
    xhr.onload = function () {
      let data = null;
      try {
        data = JSON.parse(xhr.responseText);
      } catch (e) {
        console.error('[AuthService] Failed to parse login response:', xhr.responseText);
        return reject(new Error('Invalid response from server'));
      }
      
      if (xhr.status >= 200 && xhr.status < 300) {
        return resolve(data);
      }
      const detail = (data && data.detail)
        || (xhr.status === 401 ? 'Invalid email or password' : 'Login failed');
      return reject(new Error(detail));
    };
    
    xhr.onerror = function () {
      console.error('[AuthService] Login network error');
      reject(new Error('Unable to connect to server. Please check your internet connection.'));
    };
    
    xhr.send(JSON.stringify({ email, password }));
  });
};

/**
 * Sign up with email/password
 * @param {string} email 
 * @param {string} password 
 * @param {string} name 
 * @returns {Promise<object>}
 */
export const signup = async (email, password, name = '') => {
  // Use XMLHttpRequest to bypass any fetch interceptors that may consume the
  // response body before we can read it.
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BACKEND_URL}/api/auth/signup`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    
    xhr.onload = function () {
      let data = null;
      try {
        data = JSON.parse(xhr.responseText);
      } catch (e) {
        console.error('[AuthService] Failed to parse signup response:', xhr.responseText);
        return reject(new Error('Invalid response from server'));
      }
      
      if (xhr.status >= 200 && xhr.status < 300) {
        return resolve(data);
      }
      const detail = (data && data.detail) || 'Signup failed. Please try again.';
      return reject(new Error(detail));
    };
    
    xhr.onerror = function () {
      console.error('[AuthService] Signup network error');
      reject(new Error('Unable to connect to server. Please check your internet connection.'));
    };
    
    xhr.send(JSON.stringify({ email, password, name }));
  });
};

/**
 * Logout
 * @param {string} sessionToken 
 * @returns {Promise<void>}
 */
export const logout = async (sessionToken) => {
  if (!sessionToken) return;
  
  try {
    await fetch(`${BACKEND_URL}/api/auth/logout`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${sessionToken}` }
    });
  } catch (err) {
    console.error('[AuthService] Logout error:', err);
  }
};

/**
 * Verify session token with timeout
 * @param {string} sessionToken 
 * @returns {Promise<object>}
 */
export const verifySession = async (sessionToken) => {
  // Add timeout to prevent hanging forever
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
  
  try {
    console.log('[AuthService] Verifying session...');
    const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
      headers: { 'Authorization': `Bearer ${sessionToken}` },
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      console.log('[AuthService] Session invalid, status:', response.status);
      throw new Error('Session invalid');
    }
    
    const data = await response.json();
    console.log('[AuthService] Session verified for user:', data.user_id);
    return data;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      console.error('[AuthService] Session verification timed out');
      throw new Error('Session verification timed out');
    }
    console.error('[AuthService] Session verification failed:', error.message);
    throw error;
  }
};

/**
 * Initiate Google OAuth flow
 * @returns {Promise<{auth_url: string, session_id: string}>}
 */
export const initiateGoogleAuth = async () => {
  const response = await fetch(`${BACKEND_URL}/api/auth/google/initiate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' }
  });
  
  if (!response.ok) {
    throw new Error('Failed to initiate Google auth');
  }
  
  return response.json();
};

/**
 * Poll for Google OAuth completion
 * @param {string} sessionId 
 * @returns {Promise<object|null>}
 */
export const pollGoogleAuth = async (sessionId) => {
  const response = await fetch(
    `${BACKEND_URL}/api/auth/google/poll?session_id=${sessionId}`
  );
  
  if (!response.ok) {
    return null;
  }
  
  return response.json();
};

/**
 * Get user entitlements (tier, features)
 * @param {string} userId 
 * @returns {Promise<object>}
 */
export const getEntitlements = async (userId) => {
  const response = await fetch(`${BACKEND_URL}/api/entitlements/${userId}`);
  
  if (!response.ok) {
    throw new Error('Failed to fetch entitlements');
  }
  
  return response.json();
};

// Storage helpers
export const getStoredToken = () => localStorage.getItem('session_token');
export const setStoredToken = (token) => localStorage.setItem('session_token', token);
export const clearStoredToken = () => localStorage.removeItem('session_token');

export const getStoredUserId = () => localStorage.getItem('calorie_tracker_user_id');
export const setStoredUserId = (id) => localStorage.setItem('calorie_tracker_user_id', id);
export const clearStoredUserId = () => localStorage.removeItem('calorie_tracker_user_id');

export default {
  login,
  signup,
  logout,
  verifySession,
  initiateGoogleAuth,
  pollGoogleAuth,
  getEntitlements,
  getStoredToken,
  setStoredToken,
  clearStoredToken,
  getStoredUserId,
  setStoredUserId,
  clearStoredUserId
};
